/**
 * Shared word-order utility for all slug builders.
 *
 * Word order patterns:
 *   SVO (default): subject-phrase-year  → "indonesia-public-holidays-2026"
 *   OSV (id, ms):  phrase-year-subject  → "hari-libur-2026-indonesia"
 *   OVS (ar):      phrase-subject-year  → "alutl-indunisiya-2026"
 */

export type SlugSegments = {
  subject: string;   // entity name (country, city, university)
  phrase: string;     // descriptive phrase (holidays, long-weekends, etc.)
  year?: string;      // optional year segment
};

export function buildLocalizedSlug(segments: SlugSegments, lang: string): string {
  const { subject, phrase, year } = segments;
  const parts = year
    ? getPartsWithYear(subject, phrase, year, lang)
    : getPartsNoYear(subject, phrase, lang);
  return parts.join('-');
}

function getPartsWithYear(subject: string, phrase: string, year: string, lang: string): string[] {
  switch (lang) {
    case 'id': case 'ms':
      return [phrase, year, subject];
    case 'ar':
      return [phrase, subject, year];
    default:
      return [subject, phrase, year];
  }
}

function getPartsNoYear(subject: string, phrase: string, lang: string): string[] {
  switch (lang) {
    case 'id': case 'ms': case 'ar':
      return [phrase, subject];
    default:
      return [subject, phrase];
  }
}

/**
 * Display-string variant: space-separated instead of hyphen-separated.
 * Used for pageTitle word order.
 */
export type DisplaySegments = {
  subject: string;
  phrase: string;
  year?: string | number;
};

export function buildLocalizedDisplay(segments: DisplaySegments, lang: string): string {
  const { subject, phrase, year } = segments;
  const yearStr = year != null ? String(year) : undefined;
  const parts = yearStr
    ? getPartsWithYear(subject, phrase, yearStr, lang)
    : getPartsNoYear(subject, phrase, lang);
  return parts.join(' ');
}
